import { TableCell, TableRow, TableSortLabel } from "@mui/material";
import type { LastElementTableAPIData } from "components/shared/LastElementTable/type";
import StyledCheckbox from "components/shared/StyledCheckbox";
import useWindowSize from "hooks/useWindowSize";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

interface ITableHeadRow {
    selectedData: {
        allSelected: boolean;
        setAllSelected: Dispatch<SetStateAction<boolean>>;
        handleSetAllSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
    };
    APIData: LastElementTableAPIData;
    showCustomStatusColumn: boolean;
}

/**
 *
 * Head row within AdvancePaymentsTable.
 */
const TableHeadRow = ({ selectedData, APIData, showCustomStatusColumn }: ITableHeadRow) => {
    const [t] = useTranslation();
    const windowSize = useWindowSize();

    const { handleSort, sortBy, sortOrder } = APIData;

    const tHeadStyles = {
        maxWidth: 200,
        fontSize: "12px",
        width: "min-content",
        padding: "10px 16px",
        backgroundColor: "#F8F8F8",
        fontFamily: "'Poppins', sans-serif",
        color: "#676767",
    };

    const { allSelected, handleSetAllSelected } = selectedData;

    return (
        <TableRow>
            <TableCell
                sx={{
                    ...tHeadStyles,
                    padding: "10px",
                    textAlign: "center",
                    position: "sticky",
                    left: 0,
                    minWidth: "36px",
                }}
            >
                <StyledCheckbox onChange={handleSetAllSelected} checked={allSelected} size="small" />
            </TableCell>
            <TableCell
                align="left"
                sx={{
                    ...tHeadStyles,
                    position: "sticky",
                    left: "36px",
                    minWidth: "142px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        lineHeight: "16px",
                    }}
                >
                    <span style={{ fontWeight: "500" }}>{t("AdvancePayment.Author")}</span>
                    <span style={{ fontSize: "10px", lineHeight: "10px" }}>{t(`Management.Representative`)}</span>
                </div>
            </TableCell>
            <TableCell
                align="left"
                sx={{
                    ...tHeadStyles,
                    position: "sticky",
                    left: "178px",
                    minWidth: "140px",
                }}
            >
                {t("AdvancePayment.Title")}
            </TableCell>
            <TableCell
                align="right"
                sx={{
                    ...tHeadStyles,
                    borderRight: "2px solid var(--border-color)",
                    position: "sticky",
                    left: "338px",
                    right: "495px",
                }}
            >
                {"n°"}
            </TableCell>
            <TableCell align="left" sx={{ ...tHeadStyles }}>
                {t("AdvancePayment.Type")}
            </TableCell>
            <TableCell align="right" sx={{ ...tHeadStyles, paddingLeft: "5px" }}>
                <TableSortLabel
                    active={sortBy === "amount"}
                    direction={sortOrder?.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleSort("paymentDate")}
                >
                    <span style={{ fontWeight: "500" }}>{t("AdvancePayment.Amount")}</span>
                </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ ...tHeadStyles, paddingLeft: "5px" }}>
                <TableSortLabel
                    active={sortBy === "netToPay"}
                    direction={sortOrder?.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleSort("paymentDate")}
                >
                    <span style={{ fontWeight: "500" }}>{t("AdvancePayment.NetAmount")}</span>
                </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={tHeadStyles}>
                <TableSortLabel
                    active={sortBy === "paymentDate"}
                    direction={sortOrder?.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleSort("paymentDate")}
                >
                    <span style={{ fontWeight: "500" }}>{t("AdvancePayment.PaymentDate")}</span>
                </TableSortLabel>
            </TableCell>
            <TableCell align="left" sx={tHeadStyles}>
                {t("AdvancePayment.Status")}
            </TableCell>
            {showCustomStatusColumn && (
                <TableCell align="left" sx={tHeadStyles}>
                    {t("AdvancePayment.CustomStatus")}
                </TableCell>
            )}
            <TableCell align="left" sx={tHeadStyles}>
                {windowSize.width > 1600 ? t("ImportData.Comment") : ""}
            </TableCell>
            <TableCell align="center" sx={{ ...tHeadStyles }}></TableCell>
        </TableRow>
    );
};

export default TableHeadRow;
